import { ClientMsgKind, type Player } from '@infiltre/shared';
import { useState } from 'react';
import { selectAliveCount, selectRoom, selectValidVoteTargets } from '@/entities/room';
import { VoteBoard } from '@/features/game-round';
import { useGameStore, useSessionStore } from '@/shared/hooks/use-shallow-store';
import { SoundCue, useSound } from '@/shared/lib/audio';
import { selectCurrentVoter } from '../model/selectors';
import { PassDeviceCard } from '../ui/PassDeviceCard';

/**
 * Standalone vote orchestrator. Two-stage per voter:
 *  1. Pass-device dossier — "HAND DEVICE TO X" + I'm ready CTA.
 *  2. VoteBoard — pick a target. Vote dispatches and advances.
 */
export const VoteOrchestrator = () => {
  const voter = useGameStore(selectCurrentVoter);
  const room = useGameStore(selectRoom);
  const aliveCount = useGameStore(selectAliveCount);
  const handoffIndex = useGameStore((s) => (s.handoff.kind === 'vote' ? s.handoff.index : -1));
  const candidates = useGameStore((s) => (voter ? selectValidVoteTargets(s, voter.id) : []));

  if (!voter || !room) return null;

  return (
    <VoteStage
      key={voter.id}
      voter={voter}
      candidates={candidates}
      step={handoffIndex + 1}
      total={aliveCount}
    />
  );
};

const VoteStage = ({
  voter,
  candidates,
  step,
  total,
}: {
  voter: Player;
  candidates: readonly Player[];
  step: number;
  total: number;
}) => {
  const playSound = useSound();
  const driver = useSessionStore((s) => s.driver);
  const advanceVote = useGameStore((s) => s.advanceVote);
  const [stage, setStage] = useState<'pass' | 'vote'>('pass');

  if (!driver) return null;

  if (stage === 'pass') {
    return (
      <PassDeviceCard
        playerName={voter.name}
        step={step}
        total={total}
        fileNumber={(voter.order ?? 0) + 1}
        onReady={() => {
          playSound(SoundCue.OpenVote);
          setStage('vote');
        }}
      />
    );
  }

  return (
    <VoteBoard
      voter={voter}
      candidates={candidates}
      onVote={(target) => {
        playSound(SoundCue.VoteCast);
        driver.send({
          kind: ClientMsgKind.CastVote,
          actorPlayerId: voter.id,
          targetPlayerId: target,
        });
        advanceVote();
      }}
    />
  );
};
